import type { ComponentProps } from 'react';
import type { ColumnDef } from '@tanstack/react-table';

import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import { Badge } from '@src/design-system/badge';
import { Text } from '@src/design-system/text';
import {
  REMINDER_ENTITY_STATUSES,
  type TReminder,
} from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { getAudienceLabel } from '../../reminders-dashboard/upcoming-reminders/upcoming-reminders-columns';
import {
  formatReminderTableDate,
  formatReminderTableTime,
} from '../shared/reminder-date-format';
import { ReminderChannelCell } from '../shared/reminder-channel-cell';
import { cn } from '@src/design-system/helpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@src/design-system/tooltip';

function countCell(value: number) {
  if (!value) return <span className='text-sm text-gray-700'>—</span>;
  return (
    <span className='text-sm font-medium tabular-nums text-gray-800'>
      {value}
    </span>
  );
}

function failedCountCell(value: number) {
  if (!value) return <span className='text-sm text-gray-700'>—</span>;
  return (
    <span
      className={
        value > 0
          ? 'text-sm font-semibold tabular-nums text-red-600'
          : 'text-sm font-medium tabular-nums text-gray-800'
      }
    >
      {value}
    </span>
  );
}

function BulkReminderStatusCell({ reminder }: { reminder: TReminder }) {
  const badgeClassName = 'p-2 rounded-[8px]';
  const s = reminder.status;
  if (s == null) {
    return <span className='text-sm text-gray-500'>—</span>;
  }
  const statusConfig: Record<
    string,
    {
      label: string;
      variant: ComponentProps<typeof Badge>['variant'];
      className?: string;
    }
  > = {
    [REMINDER_ENTITY_STATUSES.scheduled]: {
      label: 'Scheduled',
      variant: 'warning',
    },
    [REMINDER_ENTITY_STATUSES.failed]: {
      label: 'Failed',
      variant: 'danger',
      className: 'text-pink-500! bg-pink-100! ',
    },
    [REMINDER_ENTITY_STATUSES.sent]: {
      label: 'Sent',
      variant: 'secondary',
      className: 'text-blue-500! bg-blue-100!',
    },
    [REMINDER_ENTITY_STATUSES.completed]: {
      label: 'Completed',
      variant: 'success',
      className: 'text-green-500! bg-green-100!',
    },
    [REMINDER_ENTITY_STATUSES.cancelled]: {
      label: 'Cancelled',
      variant: 'outline',
      className: 'text-gray-800! bg-gray-100!',
    },
    [REMINDER_ENTITY_STATUSES.pending]: {
      label: 'Pending',
      variant: 'default',
      className: 'text-gray-800! bg-gray-100!',
    },
  };
  const cfg = statusConfig[String(s)] ?? {
    label: formatTextForDisplay(String(s)),
    variant: 'default' as const,
  };
  return (
    <Badge
      variant={cfg.variant}
      className={cn(badgeClassName, cfg.className)}
    >
      {cfg.label}
    </Badge>
  );
}

export const bulkRemindersColumns: ColumnDef<TReminder>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Reminder Name',
    cell: ({ row }) => (
      <span className='line-clamp-2 max-w-[220px] text-sm font-medium text-gray-800'>
        {row.original.name}
      </span>
    ),
  },
  {
    id: 'createdBy',
    accessorFn: (row) =>
      `${row.created_by?.first_name ?? ''} ${row.created_by?.last_name ?? ''}`.trim(),
    header: 'Created By',
    cell: ({ row }) => {
      const u = row.original.created_by;
      const name = `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim() || '—';
      const initials = name
        .split(' ')
        .filter(Boolean)
        .map((p) => p[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
      return (
        <div className='flex min-w-0 max-w-[200px] items-center gap-2'>
          <Avatar className='size-8 shrink-0'>
            {u?.avatar ? (
              <AvatarImage
                src={u.avatar}
                alt=''
              />
            ) : null}
            <AvatarFallback className='text-xs'>
              {initials || '?'}
            </AvatarFallback>
          </Avatar>
          <Text className='truncate text-sm font-medium text-gray-800'>
            {name}
          </Text>
        </div>
      );
    },
  },
  {
    id: 'channel',
    accessorKey: 'notification_channel',
    header: 'Channel',
    cell: ({ row }) => (
      <ReminderChannelCell channel={row.original.notification_channel} />
    ),
  },
  {
    id: 'audience',
    accessorFn: (row) => getAudienceLabel(row),
    header: 'Audience',
    enableSorting: false,
    cell: ({ row }) => (
      <span className='line-clamp-2 text-sm text-gray-800'>
        {getAudienceLabel(row.original)}
      </span>
    ),
  },
  {
    id: 'savedView',
    accessorKey: 'view.name',
    header: 'Saved View',
    enableSorting: false,
    cell: ({ row }) => (
      <span className='line-clamp-2 max-w-[200px] text-sm text-gray-800'>
        {row.original.view?.name || '—'}
      </span>
    ),
  },
  {
    id: 'scheduled',
    accessorKey: 'date',
    header: 'Scheduled',
    cell: ({ row }) => {
      const { date, timezone } = row.original;
      const dateLabel = formatReminderTableDate(date);
      const timeLabel = formatReminderTableTime(date, timezone);

      if (!timeLabel) {
        return (
          <span className='whitespace-nowrap text-sm text-gray-800'>
            {dateLabel}
          </span>
        );
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='cursor-default whitespace-nowrap text-sm text-gray-800'>
              {dateLabel}
            </span>
          </TooltipTrigger>
          <TooltipContent side='top'>{timeLabel}</TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    id: 'recipients',
    accessorKey: 'recipients_count',
    header: 'Recipients',
    enableSorting: false,
    cell: ({ row }) => countCell(row.original.recipients_count ?? 0),
  },
  {
    id: 'failed',
    accessorKey: 'failed_count',
    header: 'Failed',
    enableSorting: false,
    cell: ({ row }) => failedCountCell(row.original.failed_count ?? 0),
  },
  {
    id: 'sent',
    accessorKey: 'sent_count',
    header: 'Sent',
    enableSorting: false,
    cell: ({ row }) => countCell(row.original.sent_count ?? 0),
  },
  {
    id: 'delivered',
    accessorKey: 'delivered_count',
    header: 'Delivered',
    enableSorting: false,
    cell: ({ row }) => countCell(row.original.delivered_count ?? 0),
  },
  {
    id: 'read',
    accessorKey: 'read_count',
    header: 'Read',
    enableSorting: false,
    cell: ({ row }) => countCell(row.original.read_count ?? 0),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => <BulkReminderStatusCell reminder={row.original} />,
  },
];
